import React from 'react'
import { Typography } from '@mui/material'
import {
  Page,
  Section,
  Bullets,
  Bullet,
  Info,
  Screenshot,
} from '../../components/DocsComponents.jsx'
import DocLink from '../../components/DocLink.jsx'

const Quotes = () => (
  <Page
    title="Quotes"
    intro="One list of every quote across Event work and Patient Transport — what has
    been quoted, what is awaiting a response, and what has been accepted — so the
    pipeline is visible without opening each module."
  >
    <Section title="The quote list">
      <Typography>
        Open <DocLink url="/finance/quotes" text="Quotes" /> from the Finance
        section of the sidebar. The page shows:
      </Typography>
      <Bullets>
        <Bullet>
          A <strong>source toggle</strong> — Both, Events, or PTS.
        </Bullet>
        <Bullet>
          Summary tiles for <strong>Total Quoted</strong>,{' '}
          <strong>Awaiting Response</strong> (value of quotes sent but not yet
          answered), and <strong>Accepted</strong> (value of accepted or
          converted quotes).
        </Bullet>
        <Bullet>
          A grid with the reference, source, linked record (event or PTS
          booking, clickable), customer, status, valid-until date, and total.
        </Bullet>
      </Bullets>
      <Typography>
        Event quote statuses are <strong>Draft</strong>, <strong>Sent</strong>,{' '}
        <strong>Accepted</strong>, <strong>Declined</strong>, and{' '}
        <strong>Expired</strong>. PTS quotes have two more:{' '}
        <strong>Converted</strong> (accepted and turned into an invoice) and{' '}
        <strong>Superseded</strong> (replaced by a newer revision).
      </Typography>
      <Info title="Automatic expiry">
        Sent event quotes whose valid-until date has passed are flipped to{' '}
        <strong>Expired</strong> automatically by a daily job, so the pipeline
        figures do not keep counting lapsed quotes.
      </Info>
      <Screenshot url="finance/quotes-page.png">
        Quotes page with the source toggle, summary tiles, and quote grid.
      </Screenshot>
    </Section>

    <Section title="Where quotes are created">
      <Typography>
        This page is a monitoring view — quotes are raised and worked in their
        own modules:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Event quotes</strong> come from the proposals and quoting
          flow on events — see{' '}
          <DocLink
            url="/event-planning/proposals-and-quotes"
            text="Proposals and Quotes"
            docsPage
          />
          .
        </Bullet>
        <Bullet>
          <strong>PTS quotes</strong> are priced from tariffs against a
          booking, then sent, accepted or declined, and converted to invoices —
          see{' '}
          <DocLink
            url="/patient-transport/invoicing"
            text="PTS Quoting, Invoicing & Billing"
            docsPage
          />
          .
        </Bullet>
      </Bullets>
      <Info>
        Quotes that have been sent and are close to their valid-until date show
        up in the Finance KPIs as <strong>Quotes Expiring Soon</strong> — a
        prompt to chase the customer before the quote lapses.
      </Info>
    </Section>

    <Section title="Who can do this?">
      <Typography>
        Viewing the combined quote list requires the <strong>Finance</strong>{' '}
        team role. Creating and sending quotes is governed by the owning module
        — Event Planning team for event quotes, PTS Co-Ordinator access for PTS
        quotes.
      </Typography>
    </Section>
  </Page>
)

export default Quotes
